'use client';
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import Button from '../ui/button';

interface HeaderMenuItemProps {
  text: string;
  href: string;
  isActive?: boolean;
  onClick?: () => void;
}

const HeaderMenuItem = ({ text, href, isActive = false, onClick }: HeaderMenuItemProps) => {
  return (
    <Link data-component-id="src/components/layout/Header.tsx:17:4" data-component-path="src/components/layout/Header.tsx" data-component-line="17" data-component-end-line="27" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3Ahref%5D%22%7D"
      href={href}
      role="menuitem"
      onClick={onClick}
      className={twMerge(
        'text-base font-lato font-bold leading-lg transition-colors duration-200 hover:text-header-accent focus:outline-none focus:text-header-accent',
        isActive ? 'text-header-accent' : 'text-header-text'
      )}
    >
      {text}
    </Link>
  )
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const pathname = usePathname()

  const menuItems = [
    { text: 'Home', href: '/' },
    { text: 'About Us', href: '/about' },
    { text: 'Services', href: '/services' },
    { text: 'Contact Us', href: '/contact' },
  ]

  const handleMenuItemClick = () => {
    setIsMenuOpen(false) // Close mobile menu on item click
  }

  const handleBookConsultation = (): void => {
    const contactSection = document.getElementById('contact-section')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' })
    } else {
      // If contact section doesn't exist, navigate to home and then scroll
      window.location.href = '/#contact-section'
    }
  }

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return pathname === '/'
    }
    return pathname.startsWith(href)
  }

  return (
    <header data-component-id="src/components/layout/Header.tsx:64:4" data-component-path="src/components/layout/Header.tsx" data-component-line="64" data-component-end-line="167" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%2C%22className%22%3A%22w-full%20bg-header-background%22%7D" className="w-full bg-header-background">
      {/* Container with max-width constraint */}
      <div data-component-id="src/components/layout/Header.tsx:66:6" data-component-path="src/components/layout/Header.tsx" data-component-line="66" data-component-end-line="166" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1440px%5D%20mx-auto%22%7D" className="w-full max-w-[1440px] mx-auto">
        {/* Header content */}
        <div data-component-id="src/components/layout/Header.tsx:68:8" data-component-path="src/components/layout/Header.tsx" data-component-line="68" data-component-end-line="165" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-4%20sm%3Apx-6%20lg%3Apx-8%20py-4%22%7D" className="px-4 sm:px-6 lg:px-8 py-4">
          <div data-component-id="src/components/layout/Header.tsx:69:10" data-component-path="src/components/layout/Header.tsx" data-component-line="69" data-component-end-line="127" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20w-full%22%7D" className="flex items-center justify-between w-full">
            
            {/* Logo and Company Name Section */}
            <div data-component-id="src/components/layout/Header.tsx:72:12" data-component-path="src/components/layout/Header.tsx" data-component-line="72" data-component-end-line="81" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">

              <div data-component-id="src/components/layout/Header.tsx:74:14" data-component-path="src/components/layout/Header.tsx" data-component-line="74" data-component-end-line="80" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%22%7D" className="flex flex-col">
                <img data-component-id="src/components/layout/Header.tsx:75:16" data-component-path="src/components/layout/Header.tsx" data-component-line="75" data-component-end-line="79" data-component-file="Header.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2FbasiraLogo.png%22%2C%22alt%22%3A%22BasiraView%20Consulting%20Logo%22%2C%22className%22%3A%22w-%5B268px%5D%20h-%5B80px%5D%20object-fit%22%7D"
                  src="/images/basiraLogo.png"
                  alt="BasiraView Consulting Logo"
                  className="w-[268px] h-[80px] object-fit"
                />
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button data-component-id="src/components/layout/Header.tsx:84:12" data-component-path="src/components/layout/Header.tsx" data-component-line="84" data-component-end-line="93" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22block%20lg%3Ahidden%20p-2%20rounded-md%20hover%3Abg-background-light%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary-background%22%7D"
              className="block lg:hidden p-2 rounded-md hover:bg-background-light focus:outline-none focus:ring-2 focus:ring-primary-background"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              <svg data-component-id="src/components/layout/Header.tsx:90:14" data-component-path="src/components/layout/Header.tsx" data-component-line="90" data-component-end-line="92" data-component-file="Header.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-6%20h-6%20text-header-text%22%7D" className="w-6 h-6 text-header-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path data-component-id="src/components/layout/Header.tsx:91:16" data-component-path="src/components/layout/Header.tsx" data-component-line="91" data-component-end-line="91" data-component-file="Header.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Desktop Navigation */}
            <nav data-component-id="src/components/layout/Header.tsx:96:12" data-component-path="src/components/layout/Header.tsx" data-component-line="96" data-component-end-line="108" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20justify-center%20flex-1%22%7D" className="hidden lg:flex items-center justify-center flex-1" role="navigation">
              <div data-component-id="src/components/layout/Header.tsx:97:14" data-component-path="src/components/layout/Header.tsx" data-component-line="97" data-component-end-line="107" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-8%20lg%3Agap-12%22%7D" className="flex items-center gap-8 lg:gap-12">
                {menuItems.map((item, index) => (
                  <HeaderMenuItem data-component-id="src/components/layout/Header.tsx:99:18" data-component-path="src/components/layout/Header.tsx" data-component-line="99" data-component-end-line="105" data-component-file="Header.tsx" data-component-name="HeaderMenuItem" data-component-content="%7B%22elementName%22%3A%22HeaderMenuItem%22%7D"
                    key={index}
                    text={item.text}
                    href={item.href}
                    isActive={isActiveRoute(item.href)}
                    onClick={handleMenuItemClick}
                  />
                ))}
              </div>
            </nav>
            
            {/* CTA Button */}
            <div data-component-id="src/components/layout/Header.tsx:111:12" data-component-path="src/components/layout/Header.tsx" data-component-line="111" data-component-end-line="126" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Ablock%22%7D" className="hidden lg:block">
              <Button data-component-id="src/components/layout/Header.tsx:112:14" data-component-path="src/components/layout/Header.tsx" data-component-line="112" data-component-end-line="125" data-component-file="Header.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22hover%3Ascale-105%20active%3Ascale-95%20transition-transform%20duration-200%22%7D"
                text="Book Consultation"
                text_font_size="text-sm"
                text_font_family="Inter"
                text_font_weight="font-bold"
                text_line_height="leading-sm"
                text_text_align="center"
                text_color="text-button-text"
                fill_background_color="bg-button-secondary-bg"
                border_border_radius="rounded-md"
                padding="t=12px,r=28px,b=12px,l=28px"
                onClick={handleBookConsultation}
                className="hover:scale-105 active:scale-95 transition-transform duration-200"
              />
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <nav data-component-id="src/components/layout/Header.tsx:130:10" data-component-path="src/components/layout/Header.tsx" data-component-line="130" data-component-end-line="164" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
            className={`${isMenuOpen ? 'block' : 'hidden'} lg:hidden mt-4 pt-4 border-t border-input-border`}
            role="navigation"
          >
            <div data-component-id="src/components/layout/Header.tsx:134:12" data-component-path="src/components/layout/Header.tsx" data-component-line="134" data-component-end-line="163" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20space-y-3%22%7D" className="flex flex-col space-y-3">
              {menuItems.map((item, index) => (
                <HeaderMenuItem data-component-id="src/components/layout/Header.tsx:136:16" data-component-path="src/components/layout/Header.tsx" data-component-line="136" data-component-end-line="142" data-component-file="Header.tsx" data-component-name="HeaderMenuItem" data-component-content="%7B%22elementName%22%3A%22HeaderMenuItem%22%7D"
                  key={index}
                  text={item.text}
                  href={item.href}
                  isActive={isActiveRoute(item.href)}
                  onClick={handleMenuItemClick}
                />
              ))}
              
              {/* Mobile CTA Button */}
              <div data-component-id="src/components/layout/Header.tsx:146:14" data-component-path="src/components/layout/Header.tsx" data-component-line="146" data-component-end-line="162" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-3%20border-t%20border-input-border%22%7D" className="pt-3 border-t border-input-border">
                <Button data-component-id="src/components/layout/Header.tsx:147:16" data-component-path="src/components/layout/Header.tsx" data-component-line="147" data-component-end-line="161" data-component-file="Header.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22hover%3Ascale-105%20active%3Ascale-95%20transition-transform%20duration-200%22%7D"
                  text="Book Consultation"
                  text_font_size="text-sm"
                  text_font_family="Inter"
                  text_font_weight="font-bold"
                  text_line_height="leading-sm"
                  text_text_align="center"
                  text_color="text-button-text"
                  fill_background_color="bg-button-secondary-bg"
                  border_border_radius="rounded-md"
                  padding="t=12px,r=28px,b=12px,l=28px"
                  layout_width="100%"
                  onClick={handleBookConsultation}
                  className="hover:scale-105 active:scale-95 transition-transform duration-200"
                />
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  )
}

export default Header